import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { CreditNoteLinks } from '@/components/invoices/CreditNoteLinks';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { TABLE_CELL, TABLE_CELL_MUTED, TABLE_HEADER } from '@/components/ui/pageChrome';
import { FORM_LABEL_TYPE, TYPE, UI_MUTED, UI_SECONDARY, UI_SECTION } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { uiDateLocale } from '@/lib/i18n';
import {
  formatMoney,
  useInvoicePayments,
  useInvoiceCreditNotes,
  useRecordPayment,
  useCancelInvoice,
  useRefundInvoice,
  type InvoiceRow,
} from '@/hooks/useInvoices';
import { invoiceBalanceDueCents } from '@/lib/invoiceRefund';
import { ManualPaymentFields } from '@/components/invoices/ManualPaymentFields';
import { MobilePayRequestConfirm } from '@/components/payments/MobilePayRequestConfirm';
import { usePatient } from '@/hooks/usePatients';
import {
  useInvoiceMobilePayRequest,
  useMobilePayRequestGate,
} from '@/hooks/useInvoiceMobilePayRequest';
import {
  isPaymentDateOnOrBeforeToday,
  isPaymentRequestMethod,
  paymentDateInputToIso,
  recordedPaymentMethod,
  storedPaymentMethodLabel,
  todayDateInputValue,
} from '@/lib/manualPaymentMethods';
import { mobilePayRequestBlock } from '@/lib/mobilePayRequest';
import { PaymentRequestList } from '@/components/payments/PaymentRequestList';

interface PaymentManagementProps {
  invoice: InvoiceRow;
  canManage: boolean;
  onOpenPayment?: () => void;
  onOpenRefund?: () => void;
}

export function PaymentManagement({ invoice, canManage, onOpenPayment, onOpenRefund }: PaymentManagementProps) {
  const { t } = useTranslation();
  const { data: payments, isLoading } = useInvoicePayments(invoice.id);
  const { data: creditNotes } = useInvoiceCreditNotes(invoice.id);
  const { data: patient } = usePatient(invoice.patient_id);
  const recordPayment = useRecordPayment();
  const requestMobilePay = useInvoiceMobilePayRequest();
  const mobilePayGate = useMobilePayRequestGate(invoice.clinic_id);
  const cancelInvoice = useCancelInvoice();
  const refundInvoice = useRefundInvoice();

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);

  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentOther, setPaymentOther] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentDate, setPaymentDate] = useState(() => todayDateInputValue());
  const [mobilePayConfirm, setMobilePayConfirm] = useState(false);

  const [cancelReason, setCancelReason] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');

  const totalPaid = invoice.total_paid_cents ?? 0;
  const creditedCents = (creditNotes ?? []).reduce((sum, note) => sum + note.total_cents, 0);
  const remainingBalance = invoiceBalanceDueCents({
    status: invoice.status,
    totalCents: invoice.total_cents,
    paidCents: totalPaid,
    creditedCents,
  });
  const isPayable = ['issued', 'partial', 'overdue', 'expired_pay_link'].includes(invoice.status);
  const isCancellable = !['paid', 'void', 'cancelled', 'refunded'].includes(invoice.status);
  const isRefundable = ['paid', 'partial'].includes(invoice.status) && totalPaid > 0;

  const handleRecordPayment = async (event: FormEvent) => {
    event.preventDefault();
    const amountCents = Math.round(parseFloat(paymentAmount) * 100);
    if (isNaN(amountCents) || amountCents <= 0) {
      toast.error(t('invoices.payment_invalid_amount', 'Invalid payment amount'));
      return;
    }

    if (amountCents > remainingBalance) {
      toast.error(t('invoices.payment_exceeds_balance', 'Payment exceeds remaining balance'));
      return;
    }

    if (isPaymentRequestMethod(paymentMethod)) {
      setMobilePayConfirm(true);
      return;
    }

    const method = recordedPaymentMethod(paymentMethod, paymentOther);
    if (!method) {
      toast.error(t('invoices.payment_method_required', 'Select a payment method'));
      return;
    }
    if (!isPaymentDateOnOrBeforeToday(paymentDate)) {
      toast.error(
        t('invoices.payment_date_invalid', 'Choose a payment date that is not in the future'),
      );
      return;
    }

    try {
      await recordPayment.mutateAsync({
        invoice_id: invoice.id,
        amount_cents: amountCents,
        payment_date: paymentDateInputToIso(paymentDate),
        payment_method: method,
        payment_reference: paymentReference || undefined,
        notes: paymentNotes || undefined,
      });
      toast.success(t('invoices.payment_recorded', 'Payment recorded successfully'));
      setShowPaymentModal(false);
      setPaymentAmount('');
      setPaymentMethod('');
      setPaymentOther('');
      setPaymentReference('');
      setPaymentNotes('');
      setPaymentDate(todayDateInputValue());
    } catch (err: any) {
      toast.error(err.message || t('invoices.payment_error', 'Failed to record payment'));
    }
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) {
      toast.error(t('invoices.cancel_reason_required', 'Cancellation reason is required'));
      return;
    }

    try {
      await cancelInvoice.mutateAsync({
        invoice_id: invoice.id,
        reason: cancelReason,
      });
      toast.success(t('invoices.cancelled', 'Invoice cancelled'));
      setShowCancelModal(false);
      setCancelReason('');
    } catch (err: any) {
      toast.error(err.message || t('invoices.cancel_error', 'Failed to cancel invoice'));
    }
  };

  const handleRefund = async () => {
    if (!refundReason.trim()) {
      toast.error(t('invoices.refund_reason_required', 'Refund reason is required'));
      return;
    }

    const refundCents = refundAmount ? Math.round(parseFloat(refundAmount) * 100) : totalPaid;

    if (isNaN(refundCents) || refundCents <= 0) {
      toast.error(t('invoices.refund_invalid_amount', 'Invalid refund amount'));
      return;
    }

    if (refundCents > totalPaid) {
      toast.error(t('invoices.refund_exceeds_paid', 'Refund amount exceeds total paid'));
      return;
    }

    try {
      await refundInvoice.mutateAsync({
        invoice_id: invoice.id,
        reason: refundReason,
        refund_amount_cents: refundAmount ? refundCents : undefined,
      });
      toast.success(t('invoices.refunded', 'Invoice refunded'));
      setShowRefundModal(false);
      setRefundAmount('');
      setRefundReason('');
    } catch (err: any) {
      toast.error(err.message || t('invoices.refund_error', 'Failed to refund invoice'));
    }
  };

  return (
    <div className="space-y-4">
      {/* Payment Summary */}
      <div className="rounded-lg border border-ds-line bg-ds-card p-4">
        <div className="flex items-center justify-between">
          <h3 className={UI_SECTION}>
            {t('invoices.payment_status', 'Payment Status')}
          </h3>
          <div className="flex items-center gap-2">
            {canManage && isRefundable && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  if (onOpenRefund) {
                    onOpenRefund();
                    return;
                  }
                  setShowRefundModal(true);
                }}
              >
                {t('payments.refund_action', 'Refundér betaling')}
              </Button>
            )}
            {canManage && isPayable && remainingBalance > 0 && (
              <Button
                size="sm"
                onClick={() => {
                  if (onOpenPayment) {
                    onOpenPayment();
                    return;
                  }
                  setPaymentDate(todayDateInputValue());
                  setShowPaymentModal(true);
                }}
                disabled={remainingBalance <= 0}
              >
                <Plus className="mr-1 h-4 w-4" />
                {t('invoices.record_payment', 'Record Payment')}
              </Button>
            )}
          </div>
        </div>

        <div className="mt-3">
          <PaymentRequestList invoiceId={invoice.id} />
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <dt className={UI_MUTED}>
              {t('invoices.total_amount', 'Total Amount')}
            </dt>
            <dd className="mt-1 text-xl font-semibold text-ds-text">
              {formatMoney(invoice.total_cents, invoice.currency)}
            </dd>
          </div>
          <div>
            <dt className={UI_MUTED}>
              {t('invoices.amount_paid', 'Amount Paid')}
            </dt>
            <dd className="mt-1 text-xl font-semibold text-ds-green-ink">
              {formatMoney(totalPaid, invoice.currency)}
            </dd>
          </div>
          <div>
            <dt className={UI_MUTED}>
              {t('invoices.remaining_balance', 'Remaining Balance')}
            </dt>
            <dd className="mt-1 text-xl font-semibold text-ds-amber-ink">
              {formatMoney(remainingBalance, invoice.currency)}
            </dd>
          </div>
        </div>

        {invoice.status === 'refunded' ? (
          <div className="mt-3" data-refunded-invoice="">
            <p className={TYPE.body}>{t('invoices.status_refunded')}</p>
            <p className={UI_MUTED}>{t('invoices.refunded_settled')}</p>
            <CreditNoteLinks notes={creditNotes ?? []} />
          </div>
        ) : (
          <CreditNoteLinks notes={creditNotes ?? []} />
        )}

        {invoice.status === 'overdue' && (
          <div className={cn('mt-3 flex items-center gap-2 rounded-md bg-ds-amber-tint p-3 text-ds-amber-ink', TYPE.body)}>
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>
              {t('invoices.overdue_message', 'This invoice is overdue. Due date:')}{' '}
              {invoice.due_at ? new Date(invoice.due_at).toLocaleDateString(uiDateLocale()) : '—'}
            </span>
          </div>
        )}
      </div>

      {/* Payment History */}
      <div className="rounded-lg border border-ds-line bg-ds-card p-4">
        <h3 className={UI_SECTION}>
          {t('invoices.payment_history', 'Payment History')}
        </h3>

        {isLoading ? (
          <div className="mt-4 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-ds-muted" />
          </div>
        ) : !payments || payments.length === 0 ? (
          <p className={`mt-4 ${UI_MUTED}`}>
            {t('invoices.no_payments', 'No payments recorded yet')}
          </p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-ds-line">
              <thead>
                <tr>
                  <th className={cn('px-3 py-2 text-left uppercase tracking-wide', TABLE_HEADER)}>
                    {t('invoices.payment_date', 'Date')}
                  </th>
                  <th className={cn('px-3 py-2 text-left uppercase tracking-wide', TABLE_HEADER)}>
                    {t('invoices.payment_amount', 'Amount')}
                  </th>
                  <th className={cn('px-3 py-2 text-left uppercase tracking-wide', TABLE_HEADER)}>
                    {t('invoices.payment_method_label', 'Method')}
                  </th>
                  <th className={cn('px-3 py-2 text-left uppercase tracking-wide', TABLE_HEADER)}>
                    {t('invoices.payment_reference', 'Reference')}
                  </th>
                  <th className={cn('px-3 py-2 text-left uppercase tracking-wide', TABLE_HEADER)}>
                    {t('invoices.payment_notes', 'Notes')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ds-line">
                {payments.map((payment) => (
                  <tr key={payment.id}>
                    <td className={cn('whitespace-nowrap px-3 py-3', TABLE_CELL)}>
                      {new Date(payment.payment_date).toLocaleDateString(uiDateLocale())}
                    </td>
                    <td className={cn('whitespace-nowrap px-3 py-3 font-medium', TABLE_CELL)}>
                      {formatMoney(payment.amount_cents, payment.currency)}
                    </td>
                    <td className={cn('whitespace-nowrap px-3 py-3', TABLE_CELL_MUTED)}>
                      {storedPaymentMethodLabel(payment.payment_method, t)}
                    </td>
                    <td className={cn('whitespace-nowrap px-3 py-3', TABLE_CELL_MUTED)}>
                      {payment.payment_reference || '—'}
                    </td>
                    <td className={cn('px-3 py-3', TABLE_CELL_MUTED)}>
                      {payment.notes || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {canManage && (
        <div className="flex flex-wrap gap-2">
          {isCancellable && (
            <Button variant="secondary" size="sm" onClick={() => setShowCancelModal(true)}>
              {t('invoices.cancel_invoice', 'Cancel Invoice')}
            </Button>
          )}
          {isRefundable && !onOpenRefund && (
            <Button variant="secondary" size="sm" onClick={() => setShowRefundModal(true)}>
              {t('invoices.refund_invoice', 'Refund Invoice')}
            </Button>
          )}
        </div>
      )}

      {/* Record Payment Modal */}
      {showPaymentModal && (
        <Modal
          onClose={() => setShowPaymentModal(false)}
          title={t('invoices.record_payment', 'Record Payment')}
        >
          {mobilePayConfirm && (
            <MobilePayRequestConfirm
              amountLabel={formatMoney(Math.max(0, remainingBalance), invoice.currency)}
              patientName={
                `${patient?.first_name ?? ''} ${patient?.last_name ?? ''}`.trim() || invoice.patient_id
              }
              block={mobilePayRequestBlock({
                ready: mobilePayGate.ready,
                mobilePayEnabled: mobilePayGate.mobilePayEnabled,
                clinioPayAvailable: mobilePayGate.clinioPayAvailable,
                outstandingCents: Math.max(0, remainingBalance),
              })}
              ready={mobilePayGate.ready}
              busy={requestMobilePay.isPending}
              onCancel={() => {
                setMobilePayConfirm(false);
                setPaymentMethod('');
              }}
              onConfirm={() => {
                void requestMobilePay
                  .mutateAsync(invoice.id)
                  .then(() => {
                    setMobilePayConfirm(false);
                    setPaymentMethod('');
                    toast.success(t('appointments.payment_choice.mobilepay_sent'));
                  })
                  .catch((err: unknown) => {
                    toast.error(
                      err instanceof Error
                        ? err.message
                        : t('appointments.payment_choice.errors.mobilepay_failed'),
                    );
                  });
              }}
            />
          )}
          <form className="space-y-4" data-manual-payment="" onSubmit={handleRecordPayment}>
            <div>
              <label className={`block ${FORM_LABEL_TYPE}`}>
                {t('invoices.payment_amount', 'Amount')} ({invoice.currency})
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max={remainingBalance / 100}
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder={t('invoices.payment_amount_max', {
                  amount: (remainingBalance / 100).toFixed(2),
                })}
              />
              <p className={`mt-1 ${UI_SECONDARY}`}>
                {t('invoices.remaining_balance', 'Remaining Balance')}:{' '}
                {formatMoney(remainingBalance, invoice.currency)}
              </p>
            </div>

            <ManualPaymentFields
              idPrefix="record-payment"
              method={paymentMethod}
              onMethodChange={(next) => {
                setPaymentMethod(next);
                if (isPaymentRequestMethod(next)) setMobilePayConfirm(true);
              }}
              otherDetail={paymentOther}
              onOtherDetailChange={setPaymentOther}
              paymentDate={paymentDate}
              onPaymentDateChange={setPaymentDate}
            />

            <div>
              <label className={`block ${FORM_LABEL_TYPE}`}>
                {t('invoices.payment_reference', 'Reference')} ({t('common.optional', 'Optional')})
              </label>
              <Input
                type="text"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                placeholder={t('invoices.payment_reference_placeholder', 'e.g., Transaction ID')}
              />
            </div>

            <div>
              <label className={`block ${FORM_LABEL_TYPE}`}>
                {t('invoices.payment_notes', 'Notes')} ({t('common.optional', 'Optional')})
              </label>
              <textarea
                rows={3}
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                className={cn('block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-ds-text shadow-sm focus:border-ds-teal focus:outline-none focus:shadow-ring', TYPE.inputValue)}
                placeholder={t(
                  'invoices.payment_notes_placeholder',
                  'Additional notes about this payment',
                )}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="secondary" type="button" onClick={() => setShowPaymentModal(false)}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button type="submit" disabled={recordPayment.isPending}>
                {recordPayment.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isPaymentRequestMethod(paymentMethod)
                  ? t('invoices.mobilepay_request_send')
                  : t('invoices.record_payment', 'Record Payment')}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Cancel Invoice Modal */}
      {showCancelModal && (
        <Modal
          onClose={() => setShowCancelModal(false)}
          title={t('invoices.cancel_invoice', 'Cancel Invoice')}
        >
          <div className="space-y-4">
            <p className={UI_MUTED}>
              {t(
                'invoices.cancel_invoice_warning',
                'This will mark the invoice as cancelled. This action cannot be undone.',
              )}
            </p>
            <div>
              <label className={`block ${FORM_LABEL_TYPE}`}>
                {t('invoices.cancel_reason', 'Reason for Cancellation')}
              </label>
              <textarea
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className={cn('block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-ds-text shadow-sm focus:border-ds-teal focus:outline-none focus:shadow-ring', TYPE.inputValue)}
                placeholder={t(
                  'invoices.cancel_reason_placeholder',
                  'Why is this invoice being cancelled?',
                )}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button onClick={handleCancel} disabled={cancelInvoice.isPending}>
                {cancelInvoice.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('invoices.cancel_invoice', 'Cancel Invoice')}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Refund Invoice Modal */}
      {showRefundModal && (
        <Modal
          onClose={() => setShowRefundModal(false)}
          title={t('invoices.refund_invoice', 'Refund Invoice')}
        >
          <div className="space-y-4">
            <p className={UI_MUTED}>
              {t(
                'invoices.refund_invoice_warning',
                'This will record a refund and update the invoice status.',
              )}
            </p>
            <div>
              <label className={`block ${FORM_LABEL_TYPE}`}>
                {t('invoices.refund_amount', 'Refund Amount')} ({invoice.currency})
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max={totalPaid / 100}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                placeholder={`${t('invoices.full_refund', 'Full refund')}: ${(totalPaid / 100).toFixed(2)}`}
              />
              <p className={`mt-1 ${UI_SECONDARY}`}>
                {t(
                  'invoices.leave_empty_full_refund',
                  'Leave empty to refund the full amount paid',
                )}
                : {formatMoney(totalPaid, invoice.currency)}
              </p>
            </div>

            <div>
              <label className={`block ${FORM_LABEL_TYPE}`}>
                {t('invoices.refund_reason', 'Reason for Refund')}
              </label>
              <textarea
                rows={3}
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                className={cn('block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-ds-text shadow-sm focus:border-ds-teal focus:outline-none focus:shadow-ring', TYPE.inputValue)}
                placeholder={t(
                  'invoices.refund_reason_placeholder',
                  'Why is this invoice being refunded?',
                )}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="secondary" onClick={() => setShowRefundModal(false)}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button onClick={handleRefund} disabled={refundInvoice.isPending}>
                {refundInvoice.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('invoices.refund_invoice', 'Refund Invoice')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
